import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { Mail } from 'lucide-react';
import { Button } from "@/components/ui/button";

interface EmailSentConfirmationData {
  invoiceNumber: string;
  recipientEmail: string;
  amount: number;
}

/**
 * Shows a visual confirmation dialog when an invoice email is sent
 */
export const showEmailSentConfirmation = ({ invoiceNumber, recipientEmail, amount }: EmailSentConfirmationData) => {
  const ToastRoot = document.createElement('div');
  ToastRoot.style.position = 'fixed';
  ToastRoot.style.zIndex = '9999';
  ToastRoot.style.inset = '0';
  document.body.appendChild(ToastRoot);
  const root = createRoot(ToastRoot);

  const ToastComponent = () => {
    const [open, setOpen] = React.useState(true);

    React.useEffect(() => {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
        root.unmount();
        if (document.body.contains(ToastRoot)) {
          document.body.removeChild(ToastRoot);
        }
      };
    }, []);

    const handleClose = () => {
      setOpen(false);
      setTimeout(() => {
        root.unmount();
        if (document.body.contains(ToastRoot)) {
          document.body.removeChild(ToastRoot);
        }
      }, 200);
    };

    const formatCurrency = (value: number) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(value);
    };

    return (
      <div 
        className={`fixed inset-0 z-[9999] flex items-center justify-center`}
        style={{ pointerEvents: open ? 'auto' : 'none' }}
      >
        <div 
          className={`absolute inset-0 bg-black/50 transition-opacity duration-200 ${open ? 'opacity-100' : 'opacity-0'}`}
          onClick={handleClose}
        />
        <div 
          className={`relative transform transition-all duration-200 ${open ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
        >
          <div className="bg-white rounded-lg shadow-xl p-6 w-[420px] relative">
            <div className="flex flex-col items-center space-y-4">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
                <Mail className="h-8 w-8 text-blue-600" />
              </div>
              <div className="pt-2 text-center space-y-2">
                <h2 className="text-2xl font-semibold text-gray-900">
                  Invoice Email Sent!
                </h2>
                <p className="text-3xl font-bold text-blue-600">
                  {formatCurrency(amount)}
                </p>
                <div className="text-sm text-gray-500 space-y-1">
                  <p>Your invoice has been sent successfully.</p>
                  <p className="font-medium">Invoice: <span className="font-mono">{invoiceNumber}</span></p>
                  <p className="font-medium">Recipient: <span className="font-mono text-xs">{recipientEmail}</span></p>
                </div>
              </div>
              <Button 
                onClick={handleClose}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white mt-4 py-6 text-lg font-medium"
              >
                OK
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  root.render(<ToastComponent />);
}; 